import React, { useEffect } from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { colors } from "src/assets";
import {
  Button,
  FormControlGroup,
  FormControlLabel,
  Typography,
} from "src/components";
import { useLogout } from "./hooks";
import { Header } from "./components";
import {
  useCognito,
  useAccount,
  useBlock,
  useLike,
  useReviewByUserAsUser,
  useReviewByUserAsServiceOwner,
} from "src/hooks";
import { useNotificationsUnreadCount } from "src/hooks";
import { tabChangeEmit } from "src/utils/event";
import { toCurrency } from "src/utils/formatter";
import LocalText from "src/local";
import alert from "src/components/alert";
import { useNavigation } from "@react-navigation/native";
import { MainTabScreenProps } from "src/navigation/types";

export type MyPageNavigationParam = undefined;

export const MyPageScreen: React.FC = () => {
  const navigation =
    useNavigation<MainTabScreenProps<"MyPage">["navigation"]>();
  const [cognitoUsername, cognitoUserAttributes] = useCognito();
  const [account] = useAccount();
  const [blockedUsers] = useBlock();
  const [likedServices] = useLike();
  const [reviewsByUserAsUser] = useReviewByUserAsUser(cognitoUsername);
  const [reviewByUserAsServiceOwner] =
    useReviewByUserAsServiceOwner(cognitoUsername);
  const notificationsUnreadCount = useNotificationsUnreadCount();

  useEffect(() => {
    const unsubscribe = navigation.addListener("focus", (e) => {
      tabChangeEmit("MyPage");
    });
    return () => {
      unsubscribe();
    };
  }, [navigation]);

  const [logout, logoutLoading] = useLogout();
  const logoutHandler = () => {
    alert(
      LocalText.screens.myPage.logoutAlertTitle,
      LocalText.screens.myPage.logoutAlertDescription,
      [
        { text: LocalText.common.cancel, style: "cancel" },
        {
          text: LocalText.screens.myPage.logoutAlertAction,
          style: "destructive",
          onPress: async () => {
            await logout();
            window.open("/", "_self");
          },
        },
      ],
      { cancelable: false }
    );
  };
  return (
    <View style={styles.container}>
      <ScrollView>
        {account ? (
          <Typography
            text={LocalText.screens.myPage.profile}
            containerStyle={{ paddingBottom: 0 }}
            variant={"section"}
          />
        ) : null}
        {account ? (
          <Header
            style={styles.header}
            name={account.name}
            email={cognitoUserAttributes.email}
            imageSource={account.image ? { uri: account.image } : undefined}
            isVerified={account.identificationStatus === "APPROVED"}
            onPress={() => navigation.push("MyProfile")}
          />
        ) : null}
        <FormControlGroup style={styles.section}>
          <FormControlLabel
            text={LocalText.screens.myPage.balance}
            detailStyle={[styles.detail, styles.detailRed]}
            detail={
              account?.balance ? `￥${toCurrency(account?.balance)}` : undefined
            }
            rightIcon={"chevron"}
            onPress={() => navigation.push("Balance")}
          />
        </FormControlGroup>
        <FormControlGroup style={styles.section}>
          <FormControlLabel
            text={LocalText.screens.myPage.favorites}
            detailStyle={styles.detail}
            detail={
              likedServices.length > 0 ? `${likedServices.length}` : undefined
            }
            rightIcon={"chevron"}
            onPress={() => navigation.push("LikedService")}
          />
          <FormControlLabel
            text={LocalText.screens.myPage.blocks}
            detailStyle={styles.detail}
            detail={
              blockedUsers.length > 0 ? `${blockedUsers.length}` : undefined
            }
            rightIcon={"chevron"}
            onPress={() => navigation.push("BlockedUsers")}
          />
          {account ? (
            <FormControlLabel
              text={LocalText.screens.myPage.salerReview}
              detailStyle={styles.detail}
              detail={
                reviewsByUserAsUser.length > 0
                  ? `${reviewsByUserAsUser.length}`
                  : undefined
              }
              rightIcon={"chevron"}
              onPress={() =>
                navigation.push("ReviewByUserAsUser", {
                  id: account.id,
                  title: LocalText.screens.myPage.salerReviewTitle,
                  notFound: LocalText.screens.myPage.salerReviewNotFound,
                })
              }
            />
          ) : null}
          {account ? (
            <FormControlLabel
              text={LocalText.screens.myPage.buyerReview}
              detailStyle={styles.detail}
              detail={
                reviewByUserAsServiceOwner.length > 0
                  ? `${reviewByUserAsServiceOwner.length}`
                  : undefined
              }
              rightIcon={"chevron"}
              onPress={() =>
                navigation.push("ReviewByUserAsServiceOwner", {
                  id: account.id,
                  title: LocalText.screens.myPage.buyerReviewTitle,
                  notFound: LocalText.screens.myPage.buyerReviewNotFound,
                })
              }
            />
          ) : null}
        </FormControlGroup>
        <FormControlGroup style={styles.section}>
          <FormControlLabel
            text={LocalText.screens.myPage.salesHistory}
            detailStyle={styles.detail}
            rightIcon={"chevron"}
            onPress={() => navigation.push("SalesHistory")}
          />
        </FormControlGroup>
        <FormControlGroup style={styles.section}>
          <FormControlLabel
            text={LocalText.screens.myPage.myAccount}
            detailStyle={styles.detail}
            rightIcon={"chevron"}
            onPress={() => navigation.push("MyAccount")}
          />
          <FormControlLabel
            text={LocalText.screens.myPage.notifications}
            detailStyle={styles.detail}
            detail={
              notificationsUnreadCount > 0
                ? `${notificationsUnreadCount}`
                : undefined
            }
            rightIcon={"chevron"}
            onPress={() => navigation.push("Notifications")}
          />
          <FormControlLabel
            text={LocalText.screens.myPage.feedback}
            detailStyle={styles.detail}
            rightIcon={"chevron"}
            onPress={() => {
              window.open(`https://choku.me/contact`, "_blank");
            }}
          />
          <FormControlLabel
            text={LocalText.screens.myPage.privacyPolicy}
            detailStyle={styles.detail}
            rightIcon={"chevron"}
            onPress={() => navigation.push("PrivacyPolicy")}
          />
          <FormControlLabel
            text={LocalText.screens.myPage.terms}
            detailStyle={styles.detail}
            rightIcon={"chevron"}
            onPress={() => navigation.push("Terms")}
          />
        </FormControlGroup>
        <Button
          onPress={logoutHandler}
          variant={"primary"}
          icon={"plus-circle-arrows"}
          title={LocalText.screens.myPage.logout}
          containerStyle={styles.btn}
          loading={logoutLoading}
        />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.gray1,
  },
  header: {
    margin: 16,
  },
  detail: {
    fontWeight: "bold",
  },
  detailRed: {
    color: colors.chokuRed,
  },
  section: {
    marginTop: 8,
  },
  btn: {
    margin: 16,
  },
});
